import {objectFromEntries} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import {Owner} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {getStore} from 'redux-stores/Store';
import {BaseFieldTableProps, ClientSchemaField} from 'registry/field/FieldTable';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {Filter} from 'search/SearchConstants';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {capitalize} from 'util/NameUtil';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {AssaySchemaEditorQuery, AssaySchemaEditorQueryVariables} from './__generated/AssaySchemaEditorQuery';
import {AssayRunSchema, AssaySchema, SCHEMA_TYPE_NAME} from './AssaySchemaConstants';
import AssaySchemaCreateFlow from './AssaySchemaCreateFlow';
import * as AssaySchemaDAO from './AssaySchemaDAO';
import AssaySchemaToolbar from './AssaySchemaToolbar';
import ExistingAssaySchemaEditor from './ExistingAssaySchemaEditor';
import setWindowTitleForSchema from './setWindowTitleForSchema';

type RunSchemaOrInaccessible = NonNullable<AssaySchemaEditorQuery['runSchema']>;
type RunSchema = ExtractByTypename<RunSchemaOrInaccessible, 'RunSchemaNode'>;
type ResultSchemaOrInaccessible = NonNullable<AssaySchemaEditorQuery['resultSchema']>;
type ResultSchema = ExtractByTypename<ResultSchemaOrInaccessible, 'ResultSchemaNode'>;
type Organization = NonNullable<AssaySchemaEditorQuery['currentTenantOrganizations']>[number];

export interface AssaySchemaEditorProps {
  schemaId?: string;
  schemaType: LegacyAssaySchemaType.ASSAY_RESULT | LegacyAssaySchemaType.ASSAY_RUN;
  autosave: boolean;
  baseTestAttrs: {component: string};
  publishDefaultDataTableConfig: boolean;
}

export interface AssaySchemaEditorQueriedProps {
  schemaHasData: boolean; // Have any items of this schema been created?
  // Human-readable summaries of dependent computed fields, lookup fields, etc
  schemaFieldDependentsSummariesById: BaseFieldTableProps['schemaFieldDependentsSummariesById'];
  schemaPermissions: SchemaPermissionsGraphQL | null;
  organizations: Array<Organization>;
}

export interface AssaySchemaEditorConnectedProps {
  errors: Array<string> | null;
  errorMessage: string | null;
  ownerChoices: Array<Owner> | null;
  ownerId: string;
  name: string;
  sqlIdentifier: string | null;
  schema: AssaySchema | null;
  inputFileConfigs: Array<AutomationInputFileConfigSpec>;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
  fields: Array<ClientSchemaField | SchemaField> | null;
  hasUnsavedData: boolean;
  hasUnsavedInputFileConfigData: boolean;
  hasUnsavedOutputFileConfigData: boolean;
  allowCreateRunFromNotebook: boolean;
  allowInsertRunFromInbox: boolean;
  shouldRecordEquipment: boolean;
  connectionSchemaId: string | null;
  analysisTemplateId: string | null;
  analysisTemplateVariableMapping: Record<string, string> | null;
  analysisTemplateDefaultFiltersMapping: Record<string, Array<Filter>> | null;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  sessionId: string;
}

export class AssaySchemaEditorConnector extends DataConnector<
  AssaySchemaEditorProps & AssaySchemaEditorQueriedProps,
  AssaySchemaEditorConnectedProps
> {
  enhanceProps(): AssaySchemaEditorConnectedProps {
    const schema = this.props.schemaId ? AssayStore.getAssaySchema(this.props.schemaId) : null;
    const schemaPermissions = this.props.schemaPermissions;
    const schemaConfig = this.reduxState().ui.schemaConfig;
    const {
      sessionId,
      errorState,
      ownerChoices,
      inputFileConfigs,
      outputFileConfigs,
      hasUnsavedData,
      hasUnsavedInputFileConfigData,
      hasUnsavedOutputFileConfigData,
      allowCreateRunFromNotebook,
      allowInsertRunFromInbox,
      shouldRecordEquipment,
      connectionSchemaId,
      analysisTemplateId,
      analysisTemplateVariableMapping,
      analysisTemplateDefaultFiltersMapping,
    } = schemaConfig;
    let fields: Array<ClientSchemaField | SchemaField> | null = schemaConfig.fields;
    let {name, ownerId, sqlIdentifier} = schemaConfig;
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};
    if (schema) {
      ownerId = schema.organization.id;
      name = name == null ? schema.name : name;
      sqlIdentifier = sqlIdentifier == null ? schema.sqlIdentifier : sqlIdentifier;
    } else if (fields == null) {
      fields = [];
    }

    // TODO(BNCH-16136) remove this once the FieldTable can handle non-registry schema naming conventions
    const processFieldsForDisplay = (
      _fields: Array<ClientSchemaField | SchemaField>
    ): Array<ClientSchemaField | SchemaField> =>
      _fields.map((field) => ({...field, name: field.displayName}));

    if (Array.isArray(fields)) {
      fields = processFieldsForDisplay(fields);
    }

    return {
      errors,
      errorMessage,
      ownerChoices,
      ownerId: ownerId!,
      name: name == null ? '' : name,
      sqlIdentifier,
      schema,
      inputFileConfigs: inputFileConfigs == null ? [] : inputFileConfigs,
      outputFileConfigs: outputFileConfigs == null ? [] : outputFileConfigs,
      fields,
      hasUnsavedData,
      hasUnsavedInputFileConfigData,
      hasUnsavedOutputFileConfigData,
      allowCreateRunFromNotebook,
      allowInsertRunFromInbox,
      shouldRecordEquipment,
      connectionSchemaId,
      analysisTemplateId,
      analysisTemplateVariableMapping,
      analysisTemplateDefaultFiltersMapping,
      schemaPermissions,
      sessionId,
    };
  }
}

export function AssaySchemaEditor(
  props: AssaySchemaEditorProps & AssaySchemaEditorQueriedProps & AssaySchemaEditorConnectedProps
): JSX.Element {
  const [isDataStale, setIsDataStale] = useState(true);

  const unsavedDataMessage = `Your ${
    SCHEMA_TYPE_NAME[props.schemaType]
  } schema has unsaved changes. Do you want to leave the page and discard your changes?`;

  const beforeUnloadHandler = (): string | undefined => {
    if (!props.hasUnsavedData) {
      return;
    }
    return unsavedDataMessage;
  };

  useEffect(() => {
    $(window).on('beforeunload', beforeUnloadHandler);

    // this will clean up the event every time the component is re-rendered
    return function cleanup(): void {
      $(window).off('beforeunload', beforeUnloadHandler);
    };
  });

  const handleNavigateToSchemaListing = (): void => {
    BenchlingUtil.navigate(null, AssaySchemaDAO.assaySchemasURL(props.schemaType));
  };

  const {schemaId} = props;

  useEffect(() => {
    setIsDataStale(true);
    getStore().dispatch(SchemaConfigReduxActions.resetSchemaSpecUi());
    if (schemaId) {
      AssaySchemaActions.fetchSchemaSpec(schemaId, false);
    }
    setIsDataStale(false);
  }, [schemaId]);

  const schemaAnalysisTemplateId =
    props.schemaType === LegacyAssaySchemaType.ASSAY_RUN && props.schema
      ? (props.schema as AssayRunSchema).analysisTemplate?.id
      : null;

  const [effectiveAnalysisTemplateId, setEffectiveAnalysisTemplateId] = useState<string | null>(
    schemaAnalysisTemplateId ?? null
  );
  const [draftTemplateFields, setDraftTemplateFields] = useState<Array<ClientSchemaField> | null>(null);
  const [draftAnalysisTemplateVariableMapping, setDraftAnalysisTemplateVariableMapping] = useState<Record<
    string,
    string
  > | null>(null);

  useEffect(() => {
    if (schemaId) {
      setEffectiveAnalysisTemplateId(schemaAnalysisTemplateId ?? null);
    } else {
      setEffectiveAnalysisTemplateId(null);
      setDraftTemplateFields(null);
      setDraftAnalysisTemplateVariableMapping(null);
    }
  }, [schemaId, schemaAnalysisTemplateId]);

  useEffect(() => {
    if (effectiveAnalysisTemplateId == null) {
      setDraftTemplateFields(null);
      setDraftAnalysisTemplateVariableMapping(null);
    }
  }, [effectiveAnalysisTemplateId]);

  const isLoading =
    isDataStale || props.ownerChoices == null || props.fields == null || (props.schemaId && !props.schema);

  if (isLoading) {
    return <CenteredHorizontalThrobber key="throbber" />;
  } else if (!props.schemaId && props.ownerChoices!.length === 0) {
    return <NoOwnersSchemaMessage />;
  }

  const containerProps = {
    title: `${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Schemas`,
    subtitle: props.schemaId
      ? props.schema!.name
      : `Create ${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Schema`,
    onClickTitle: handleNavigateToSchemaListing,
    toolbar: props.schema && (
      <AssaySchemaToolbar schemaId={props.schema.id} schemaType={props.schemaType} isSpecJsonFormat={false} />
    ),
  };

  setWindowTitleForSchema(props.schema, props.schemaType);

  const sharedEditorProps = {
    ...props,
    unsavedDataMessage,
    effectiveAnalysisTemplateId,
    draftTemplateFields,
    draftAnalysisTemplateVariableMapping,
    draftAnalysisTemplateDefaultFiltersMapping: null,
    onEffectiveAnalysisTemplateIdChange: setEffectiveAnalysisTemplateId,
    onDraftTemplateFieldsChange: setDraftTemplateFields,
    onDraftAnalysisTemplateVariableMappingChange: setDraftAnalysisTemplateVariableMapping,
  };

  return (
    <SettingsPageContainer {...containerProps}>
      {schemaId ? (
        <ExistingAssaySchemaEditor {...sharedEditorProps} schema={props.schema!} />
      ) : (
        <AssaySchemaCreateFlow {...sharedEditorProps} organizations={props.organizations} />
      )}
    </SettingsPageContainer>
  );
}

const ConnectedAssaySchemaEditor = AssaySchemaEditorConnector.connect(AssaySchemaEditor);

export const ASSAY_SCHEMA_EDITOR_QUERY: DocumentNode = gql`
  query AssaySchemaEditorQuery($id: StrictString) @skipAudit @engComponent(name: RESULTS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
    resultSchema(id: $id) {
      ... on ResultSchemaNode {
        id
        hasData
        allFields {
          id
          apiId
          dependentsSummaries
        }
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
      }
    }
    runSchema(id: $id) {
      ... on RunSchemaNode {
        id
        hasData
        allFields {
          id
          apiId
          dependentsSummaries
        }
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
  ${getFragmentWrapper('SchemaPermissions').fragment}
`;

function isResultSchema(s?: ResultSchemaOrInaccessible | null): s is ResultSchema {
  return s?.__typename === 'ResultSchemaNode';
}

function isRunSchema(s?: RunSchemaOrInaccessible | null): s is RunSchema {
  return s?.__typename === 'RunSchemaNode';
}

function QueryingAssaySchemaEditor(props: AssaySchemaEditorProps): JSX.Element {
  return (
    <BenchlingQuery<AssaySchemaEditorQuery, AssaySchemaEditorQueryVariables>
      query={ASSAY_SCHEMA_EDITOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
      variables={{id: props.schemaId}}
    >
      {(data) => {
        const schemaHasData =
          (isResultSchema(data.resultSchema) && data.resultSchema.hasData) ||
          (isRunSchema(data.runSchema) && data.runSchema.hasData);
        const schemaFieldDependentsSummariesById = objectFromEntries(
          (
            (isResultSchema(data.resultSchema) && data.resultSchema.allFields) ||
            (isRunSchema(data.runSchema) && data.runSchema.allFields) ||
            []
          ).map((field) => [field.apiId, field.dependentsSummaries])
        );

        const schemaPermissions =
          (isResultSchema(data.resultSchema) && data.resultSchema.permissions) ||
          (isRunSchema(data.runSchema) && data.runSchema.permissions);

        const organizations = data.currentTenantOrganizations;

        return (
          <ConnectedAssaySchemaEditor
            {...props}
            schemaHasData={schemaHasData}
            schemaFieldDependentsSummariesById={schemaFieldDependentsSummariesById}
            schemaPermissions={schemaPermissions as SchemaPermissionsGraphQL}
            organizations={organizations}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export const assaySchemaEditorStyles = StyleSheet.create({
  fieldSection: {
    marginBottom: '32px',
  },
});

export default QueryingAssaySchemaEditor;
